import React, { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense && am5.addLicense("AM5C258896422");

const CompareBarLineChart = ({
  chartId,
  chartClass,
  chartData = [],
  companyName = "",
  chartLabels = [],
  showLegend = true
}) => {
  useLayoutEffect(() => {
    if (!chartId || !chartData || chartData.length === 0) return;

    let root = am5.Root.new(chartId);
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        paddingTop: 10,
        paddingRight: 30,
        paddingLeft: 10,
        paddingBottom: 40
      })
    );

    // X Axis
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "label",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
          strokeOpacity: 1,
          stroke: am5.color(0x707070),
        })
      })
    );
    xAxis.get("renderer").grid.template.setAll({ visible: false });
    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x757575),
      fontSize: 12,
      fontFamily: "Ubuntu, sans-serif"
    });

    xAxis.data.setAll(chartData);

    // Y Axis
    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 1,
          stroke: am5.color(0x707070),
        }),
        min: 0,
        numberFormat: "#,#a"
      })
    );
    yAxis.get("renderer").grid.template.setAll({ visible: false });
    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x757575),
      fontSize: 12,
      fontFamily: "Ubuntu, sans-serif"
    });

    // Shared Tooltip
    const sharedTooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      pointerOrientation: "horizontal",
      autoTextColor: false,
    });
    sharedTooltip.label.adapters.add("text", (text, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) return "";
      const ctx = dataItem.dataContext;
      return (
        `[bold]${ctx.label}[/]\n` +
        `[fontSize:12][#2196f3]●[/] [bold]Sales Value:[/] [bold]${formatNumberForTooltip(ctx.sales_value, "Sales Value")}\n` +
        `[fontSize:12][#1565c0]●[/] [bold]Sales Volume:[/] [bold]${formatNumberForTooltip(ctx.sales_volume, "Sales Volume")}[/]`
      );
    });
    sharedTooltip.get("background").setAll({
      fill: am5.color(0x263143),
      fillOpacity: 1,
      stroke: am5.color(0x263143),
      cornerRadius: 8
    });
    sharedTooltip.label.setAll({
      fill: am5.color(0xffffff),
      fontSize: 12,
      fontFamily: "Ubuntu, sans-serif",
      fontWeight: "400"
    });

    // Area Series (Sales Value)
    let areaSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Sales Value",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "sales_value",
        categoryXField: "label",
        stroke: am5.color(0x2196f3),
        fill: am5.color(0x2196f3),
        tension: 0.7,
        tooltip: sharedTooltip,
        // tooltipText is handled by sharedTooltip.label.adapters
      })
    );
    areaSeries.strokes.template.setAll({ strokeWidth: 2, stroke: am5.color(0x2196f3) });
    areaSeries.fills.template.setAll({
      visible: true,
      fillOpacity: 0.25,
      fill: am5.color(0x2196f3)
    });
    areaSeries.data.setAll(chartData);

    // Line Series (Sales Volume)
    let lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Sales Volume",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "sales_volume",
        categoryXField: "label",
        stroke: am5.color(0x1565c0),
        fill: am5.color(0x1565c0),
        tension: 0.7,
        tooltip: sharedTooltip,
        // tooltipText is handled by sharedTooltip.label.adapters
      })
    );
    lineSeries.strokes.template.setAll({ strokeWidth: 2, stroke: am5.color(0x1565c0) });
    lineSeries.fills.template.setAll({
      visible: false,
      fill: am5.color(0x1565c0)
    });
    lineSeries.data.setAll(chartData);

    // Cursor
    chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "none",
      xAxis: xAxis,
      yAxis: yAxis
    }));
    chart.get("cursor").lineY.set("visible", false);

    // ----------- Custom Legend Implementation -----------
    if (showLegend) {
      let legend = am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 10,
        marginBottom: 0,
        useDefaultMarker: true,
        layout: root.horizontalLayout, // horizontal layout
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      });

      legend.labels.template.setAll({
        fontFamily: "Ubuntu, sans-serif",
        fontSize: 12,
        fill: am5.color(0x213345),
        fontWeight: "400",
        oversizedBehavior: "wrap",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 4,   // Reduce left padding
        paddingRight: 4,  // Reduce right padding
        textAlign: "center",
      });

      legend.markers.template.setAll({
        width: 12,
        height: 12,
        strokeWidth: 2,
        fillOpacity: 1,
        strokeOpacity: 1,
        centerY: am5.p50,
        centerX: am5.p50,
      });
      legend.markerRectangles.template.setAll({
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        cornerRadiusBL: 2,
        cornerRadiusBR: 2,
        width: 12,
        height: 12,
      });



      legend.markers.template.adapters.add("stroke", (stroke, target) => {
        const series = target.dataItem?.dataContext;
        if (series) {
          return series.get("fill");
        }
        return stroke;
      });

      legend.set("background", undefined);

      legend.itemContainers.template.setAll({
        layout: root.horizontalLayout,
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        minWidth: 0,
        minHeight: 0,
        centerY: am5.p50,
        display: "flex",
        marginRight: -20
      });

      chart.bottomAxesContainer.children.push(legend);
      legend.data.setAll(chart.series.values);
    }
    // ----------------------------------------------------

    chart.zoomOutButton.set("forceHidden", true);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [chartData, chartId, chartLabels, showLegend]);

  return (
    <div className="smooth-area-chart-card" style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
      <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "Ubuntu, sans-serif", marginBottom: 8, textAlign: "center" }}>{companyName}</div>
      <div
        id={chartId}
        className={chartClass}
        style={{
          width: "100%",
          height: "220px",
          minHeight: "180px",
          maxWidth: "99vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      />
    </div>
  );
};

CompareBarLineChart.propTypes = {
  chartId: PropTypes.string,
  chartClass: PropTypes.string,
  chartData: PropTypes.array,
  companyName: PropTypes.string,
  chartLabels: PropTypes.array,
  showLegend: PropTypes.bool
};

export default CompareBarLineChart;